<template>
  <div class="international-phone-input">
    <div class="phone-field" :class="{ 'error': hasError, 'focused': isFocused }">
      <!-- Country Code Selector (appears as prefix) -->
      <div class="country-selector" @click="toggleDropdown" ref="selectorButton">
        <span :class="`fi fi-${selectedCountry.iso2}`" class="flag-icon"></span>
        <span class="country-code">+{{ selectedCountry.code }}</span>
        <svg class="dropdown-arrow" :class="{ 'open': showDropdown }" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </div>

      <!-- Phone Number Input -->
      <input
        ref="phoneInput"
        v-model="phoneNumber"
        type="tel"
        :placeholder="placeholder"
        class="phone-input"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @input="handleInput"
      />

      <!-- Dropdown Menu -->
      <div v-if="showDropdown" class="dropdown-menu" ref="dropdownMenu">
        <div class="dropdown-search">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search countries..."
            class="search-input"
            @click.stop
          />
        </div>
        <div class="dropdown-list">
          <div
            v-for="country in filteredCountries"
            :key="country.code"
            class="dropdown-item"
            :class="{ 'selected': country.code === selectedCountry.code }"
            @click="selectCountry(country)"
          >
            <span :class="`fi fi-${country.iso2}`" class="flag-icon"></span>
            <span class="country-name">{{ country.name }}</span>
            <span class="country-code">+{{ country.code }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { countryCodes, DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'

export default {
  name: 'InternationalPhoneInput',
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: ''
      })
    },
    placeholder: {
      type: String,
      default: 'Enter phone number'
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showDropdown: false,
      searchQuery: '',
      isFocused: false,
      phoneNumber: this.modelValue.phone || '',
      selectedCountryCode: this.modelValue.countryCode || DEFAULT_COUNTRY_CODE
    }
  },
  computed: {
    selectedCountry () {
      return countryCodes.find(c => c.code === this.selectedCountryCode) || countryCodes[0]
    },
    filteredCountries () {
      if (!this.searchQuery) return countryCodes

      const query = this.searchQuery.toLowerCase()
      return countryCodes.filter(country =>
        country.name.toLowerCase().includes(query) ||
        country.code.includes(query)
      )
    },
    fullPhoneNumber () {
      return `+${this.selectedCountryCode}${this.phoneNumber}`
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler (newVal) {
        if (newVal.countryCode !== this.selectedCountryCode) {
          this.selectedCountryCode = newVal.countryCode
        }
        if (newVal.phone !== this.phoneNumber) {
          this.phoneNumber = newVal.phone
        }
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown () {
      this.showDropdown = !this.showDropdown
      this.searchQuery = ''
    },
    selectCountry (country) {
      this.selectedCountryCode = country.code
      this.showDropdown = false
      this.searchQuery = ''
      this.emitValue()
      // Focus back to phone input
      this.$nextTick(() => {
        this.$refs.phoneInput?.focus()
      })
    },
    handleInput () {
      this.emitValue()
    },
    emitValue () {
      this.$emit('update:modelValue', {
        countryCode: this.selectedCountryCode,
        phone: this.phoneNumber
      })
    },
    handleClickOutside (event) {
      const selector = this.$refs.selectorButton
      const dropdown = this.$refs.dropdownMenu

      if (selector && !selector.contains(event.target) &&
          dropdown && !dropdown.contains(event.target)) {
        this.showDropdown = false
      }
    }
  }
}
</script>

<style scoped>
.international-phone-input {
  width: 100%;
}

.phone-field {
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s;
  position: relative;
  height: 54px;
}

.phone-field.focused {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.phone-field.error {
  border-color: #ef4444;
}

.country-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  cursor: pointer;
  border-right: 1px solid #e5e7eb;
  height: 100%;
  user-select: none;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.country-selector:hover {
  background-color: #f9fafb;
}

.flag-icon {
  width: 24px;
  height: 18px;
  border-radius: 2px;
  flex-shrink: 0;
  object-fit: cover;
}

.country-code {
  font-weight: 600;
  color: #1f2937;
  font-size: 15px;
  min-width: 32px;
}

.dropdown-arrow {
  transition: transform 0.2s;
  color: #6b7280;
  flex-shrink: 0;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 16px;
  font-size: 15px;
  color: #1f2937;
  background: transparent;
  height: 100%;
}

.phone-input::placeholder {
  color: #9ca3af;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.dropdown-search {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #3b82f6;
}

.dropdown-list {
  overflow-y: auto;
  flex: 1;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.dropdown-item:hover {
  background-color: #f9fafb;
}

.dropdown-item.selected {
  background-color: #eff6ff;
}

.dropdown-item .country-name {
  flex: 1;
  font-size: 14px;
  color: #1f2937;
}

.dropdown-item .country-code {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

/* Scrollbar styling */
.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f9fafb;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
